import React, { useEffect, useRef, useState } from "react"
import clsx from "clsx"
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuPopover,
  MenuLink,
} from "@reach/menu-button"
import Portal from "@reach/portal"
import "@reach/menu-button/styles.css"
import pluralize from "pluralize"
import slugify from "slugify"

import { Asset, Flag, Icon, Link } from "components"
import { getAllButLastWord, getLastWord } from "utils"

function NavItem({ label, href }) {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <MenuLink
      as={Link}
      href={href}
      className="text-darkNavy focus:outline-none hover:text-darkNavy focus:text-violet hover:bg-transparent focus:bg-transparent p-0"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <span className="h3 relative">
        {getAllButLastWord(label.split(" | ")[0])}
        <span className="whitespace-nowrap inline-flex items-center flex-shrink-0">
          {getLastWord(label.split(" | ")[0])}
          <span
            className="flex items-center justify-center"
            style={{
              width: "0.375em",
              height: "0.375em",
              marginTop: "0.25em",
              marginLeft: "0.25em",
            }}
          >
            <Icon
              className={clsx(
                "text-violet transform transition-transform ease-out duration-300",
                isHovering === true && "translate-x-1"
              )}
              symbol="arrow-right"
              size="0.375em"
            />
          </span>
        </span>
      </span>
      {label.split(" | ")[1] && (
        <div className="body text-mineralGray pt-3">
          {" "}
          {label.split(" | ")[1]}
        </div>
      )}
    </MenuLink>
  )
}

function NavLabel({ label, href }) {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <MenuLink
      className={clsx(
        "self-end p-0 mt-auto inline-block font-bold underline text-violet body hover:bg-transparent focus:outline-none focus:bg-transparent hover:text-violet focus:text-violet transition-opacity duration-150 ease-out focus:opacity-75",
        isHovering === true && "opacity-75"
      )}
      as={Link}
      href={href}
      style={{
        textDecorationThickness: "2px",
        textUnderlineOffset: "2px",
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {label}
    </MenuLink>
  )
}

function NavCard({ category, href, image, title }) {
  const [isHovering, setIsHovering] = useState(false)

  return (
    <MenuLink
      className="hover:bg-white focus:bg-white focus:outline-none p-0 bg-white"
      as={Link}
      href={href}
      style={{ maxWidth: 284 }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="aspect-w-8 aspect-h-7 bg-darkOverlay">
        <Asset
          asset={image}
          objectFit="cover"
          objectPosition="center"
          style={{
            position: "absolute",
          }}
        />
        <div className="flex items-end pl-6">
          <Flag
            label={pluralize.singular(category?.name)}
            appearance="primary"
          />
        </div>
      </div>
      <div className="px-6 py-4 space-y-2 whitespace-normal">
        <p className="lead-bold text-darkNavy">{title}</p>
        <div aria-hidden className="lead-bold">
          <Icon
            className={clsx(
              "text-violet transform transition-transform ease-out duration-300",
              isHovering === true && "translate-x-1"
            )}
            symbol="arrow-right"
            size="0.75em"
          />
        </div>
      </div>
    </MenuLink>
  )
}

function Dropdown({ featuredContent, label, links, secondaryLink }) {
  const popoverRef = useRef()
  const buttonRef = useRef()

  const [top, setTop] = useState(0)

  const handleScroll = () => {
    if (buttonRef?.current?.getAttribute("aria-expanded") === "true") {
      buttonRef?.current?.dispatchEvent(
        new MouseEvent("mousedown", {
          bubbles: true,
          cancelable: true,
          screenX: 0,
          screenY: 0,
        })
      )
    }
  }

  // handle closing all other menu buttons on open
  const checkOthers = () => {
    let headerDropdowns = document.getElementsByClassName("header-dropdown")
    if (headerDropdowns?.length > 0) {
      for (var i = 0; i < headerDropdowns.length; i++) {
        if (headerDropdowns[i].getAttribute("aria-expanded") === "true") {
          headerDropdowns[i].dispatchEvent(
            new MouseEvent("mousedown", {
              bubbles: true,
              cancelable: true,
              screenX: 0,
              screenY: 0,
            })
          )
        }
      }
    }
  }

  // handle positioning popup on open
  const handlePosition = () => {
    if (popoverRef?.current) {
      const header = document.getElementById("header")
      if (header) {
        let rect = header?.getBoundingClientRect()
        setTop(rect?.top + rect?.height + "px")
      }
    }
  }

  useEffect(() => {
    handleScroll()
    window.addEventListener("scroll", handleScroll)
    return function cleanup() {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [popoverRef])

  //rename aria-ids on nav menu
  useEffect(() => {
    if (buttonRef?.current) {
      let menuItems = document.querySelectorAll(
        '[aria-labelledby="' + buttonRef.current.id + '"]'
      )
      for (let i = 0; i < menuItems.length; i++) {
        if (i > 0) {
          menuItems[i].id = menuItems[i].id + "-" + i
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonRef.current])

  return (
    <Menu id={slugify(label?.toLowerCase())}>
      {({ isExpanded }) => {
        // add 2px border to header on expansion
        let header
        if (typeof document !== "undefined") {
          header = document?.getElementById("header")
        }
        if (header) {
          if (isExpanded) {
            setTimeout(() => {
              header.style.borderBottom = "2px solid"
              header.style.borderColor = "rgb(104, 91, 181)"
            }, 10)
          } else {
            header.style.borderBottom = ""
            header.style.borderColor = ""
          }
        }
        return (
          <>
            <MenuButton
              ref={buttonRef}
              className={clsx(
                "header-dropdown space-x-2 text-mineralGray nav-primary focus:outline-none transition-colors ease-out duration-300 focus:text-violet",
                isExpanded && "text-violet "
              )}
              onMouseDown={() => {
                handlePosition()
                checkOthers()
              }}
              onKeyDown={() => {
                handlePosition()
                checkOthers()
              }}
            >
              <span className="hover:opacity-75 transition-opacity duration-150 ease-out">
                {label}
              </span>
              <Icon
                aria-hidden
                className={clsx(
                  "text-violet transform transition-transform ease-out duration-300",
                  isExpanded && "rotate-180"
                )}
                symbol="chevron-down"
                size="0.75em"
              />
            </MenuButton>
            <MenuPopover
              ref={popoverRef}
              className="z-headerDropdown wrapper pointer-events-none"
              style={{
                position: "fixed",
                top: top,
                right: 0,
                left: 0,
              }}
              // portal={false}
            >
              <div
                className="flex bg-white border-0 pointer-events-auto"
                style={{
                  boxShadow: "0px 30px 40px rgba(0, 0, 0, 0.25)",
                }}
              >
                <div className="flex flex-col justify-between flex-1 px-16 py-10 space-y-10">
                  <MenuItems
                    className="flex flex-col items-start p-0 space-y-10 bg-transparent border-0"
                    id="menuTest1"
                  >
                    {links?.map(({ label, link }, i) => (
                      <NavItem
                        label={label}
                        href={link?.link}
                        className="h3"
                        key={`dropdownLink--${i}`}
                      />
                    ))}
                  </MenuItems>
                  {secondaryLink && (
                    <MenuItems className="p-0 bg-transparent border-0">
                      <NavLabel
                        label={secondaryLink?.label}
                        href={secondaryLink?.link?.link}
                      />
                    </MenuItems>
                  )}
                </div>
                {featuredContent && (
                  <div className="px-14 bg-lightBlue flex flex-col justify-between w-2/5 py-10 space-y-10">
                    <MenuItems className="p-0 bg-transparent border-0">
                      <NavCard
                        {...featuredContent}
                        href={
                          "/news/" +
                          (featuredContent?.category?.slug === "/"
                            ? ""
                            : featuredContent?.category?.slug + "/") +
                          featuredContent?.slug +
                          "/"
                        }
                      />
                    </MenuItems>
                    <MenuItems className="p-0 bg-transparent border-0">
                      <NavLabel
                        label={
                          "See more " +
                          featuredContent?.category?.name?.toLowerCase()
                        }
                        href={
                          "/news/" +
                          (featuredContent?.category?.slug === "/"
                            ? ""
                            : featuredContent?.category?.slug + "/")
                        }
                      />
                    </MenuItems>
                  </div>
                )}
              </div>
            </MenuPopover>
            {isExpanded && (
              <Portal>
                <div
                  className="z-subheader fixed top-0 left-0 w-full h-full"
                  style={{ backgroundColor: "rgba(0, 0, 0, 0.25)" }}
                />
              </Portal>
            )}
          </>
        )
      }}
    </Menu>
  )
}

export default Dropdown
